import { Box, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ExpensesAreaChart from '../components/charts/ExpensesAreaChart';
import PresenceRadialGauge from '../components/charts/PresenceRadialGauge';
import SitesMapChart from '../components/charts/SitesMapChart';
import TonnageBarChart from '../components/charts/TonnageBarChart';
import ValidationSankeyChart from '../components/charts/ValidationSankeyChart';
import DashboardCard from '../components/ui/DashboardCard';
import { useAuth } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import { capitalizeFirstLetter } from '../utils/capitalize';

const Dashboard: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    usePageTitle(capitalizeFirstLetter(t('dashboard')));

    const phrases = [
        "Noir c'est noir.",
        'Mourir peut attendre.',
        'Touché, mais jamais coulé.',
        "Jusqu'ici tout va bien\nJusqu'ici tout va bien\nJusqu'ici tout va bien",
        'Ce qui ne tue pas rend plus fort',
        "L'impossible n'est qu'une opinion.",
        'Tu nies, tu fuis !',
    ];

    const [alignment, setAlignment] = React.useState(0);
    const randomPhrase = phrases[Math.floor(Math.random() * phrases.length)];

    const handleChange = (_event: React.MouseEvent<HTMLElement>, newAlignment: number | null) => {
        if (newAlignment === null) {
            return;
        }
        setAlignment(newAlignment);
    };

    const getMonthRange = (alignment: number) => {
        const now = new Date();
        const targetMonth = now.getMonth() - alignment;
        const targetYear = now.getFullYear();

        const startDate = new Date(targetYear, targetMonth, 1);
        const endDate = new Date(targetYear, targetMonth + 1, 0, 23, 59, 59, 999);

        const format = (d: Date) => d.toISOString();

        return { startDate: format(startDate), endDate: format(endDate) };
    };

    const { startDate, endDate } = React.useMemo(() => getMonthRange(alignment), [alignment]);
    const today = React.useMemo(() => {
        const now = new Date();
        now.setMonth(now.getMonth() - alignment);
        return now.toISOString();
    }, [alignment]);

    return (
        <Box sx={{ overflowY: 'auto', padding: 2, gap: 2, display: 'flex', flexDirection: 'column' }}>
            <div className="fx-r fx-justify-sb fx-items-fs ">
                <div>
                    <Typography variant="h3" color="primary">
                        {capitalizeFirstLetter(t('welcome'))}, {capitalizeFirstLetter(user?.firstName) || user?.lastName.toUpperCase()}
                    </Typography>
                    <Typography variant="subtitle1" color="textDisabled">
                        {randomPhrase}
                    </Typography>
                </div>
                <div>
                    <ToggleButtonGroup value={alignment} exclusive onChange={handleChange}>
                        <ToggleButton value={0}>{capitalizeFirstLetter(t('currentMonth'))}</ToggleButton>
                        <ToggleButton value={1}>{capitalizeFirstLetter(t('previousMonth'))}</ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </div>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
                    <DashboardCard icon="public" title={capitalizeFirstLetter(t('sites'))}>
                        <SitesMapChart />
                    </DashboardCard>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 9 }}>
                    <DashboardCard icon="freeCancellation" title={capitalizeFirstLetter(t('activities'))}>
                        <ValidationSankeyChart today={today} />
                    </DashboardCard>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 7, lg: 8, xl: 10 }}>
                    <DashboardCard icon="scale" title={capitalizeFirstLetter(t('tonnage'))}>
                        <TonnageBarChart startDate={startDate} endDate={endDate} />
                    </DashboardCard>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 5, lg: 4, xl: 2 }}>
                    <DashboardCard icon="person" title={capitalizeFirstLetter(t('attendanceRate'))}>
                        <PresenceRadialGauge today={today} />
                    </DashboardCard>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <DashboardCard icon="euro" title={capitalizeFirstLetter(t('expenses'))}>
                        <ExpensesAreaChart today={today} />
                    </DashboardCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;
